import I18n from 'I18n';
import { memo, useState, useEffect } from 'react';
import CampaignSelectFormControl from 'campaigns-lib/components/campaignSelect/CampaignSelectFormControl';
import { useFetchCampaignsAssociationSettings, hasCampaignsReadAccess, hasCampaignsWriteAccess } from './hooks/useFetchCampaignsAssociationSettings';
import { useFetchAssociatedCampaigns } from './hooks/useFetchAssociatedCampaign';
import { useHandleCampaignChange } from './hooks/useHandleCampaignChange';
import { useFetchObjectPropertyDefinitions } from './hooks/useFetchObjectPropertyDefinitions';
import CampaignsPlatformSelectLabel from './CampaignsPlatformSelectLabel';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { useFetchAssociationDefinition } from './hooks/useFetchAssociationDefinition';
import { useFetchAssociatedAssetObjectId } from './hooks/useFetchAssociatedAssetObjectId';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CampaignsPlatformSelect({
  objectType,
  assetId,
  crmIdProperty = 'hs_object_id',
  onChange = () => {},
  save = true,
  editorSelect = true,
  FormLabel = undefined,
  withUIFloatingAlertList = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  className,
  //TO-DO: Remove this once dependent apps start replace it with formControlStyle
  disabled = false,
  disabledTooltip,
  formControlStyle
}) {
  // To ensure the select is not removed when we invalidate the initial campaign cache on
  // change for the other campaign platform component to automatically update.
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [selectedCampaignGuids, setSelectedCampaignGuids] = useState([]);
  const {
    data: associationSettings
  } = useFetchCampaignsAssociationSettings();
  const {
    data: objectDefinition
  } = useFetchObjectPropertyDefinitions({
    objectType
  });
  const {
    data: associationDefinition
  } = useFetchAssociationDefinition({
    associationSettings,
    objectType
  });
  const isMultiSelect = (associationDefinition === null || associationDefinition === void 0 ? void 0 : associationDefinition.cardinality) === 'ONE_TO_MANY';
  const {
    data: objectId
  } = useFetchAssociatedAssetObjectId({
    objectType,
    assetId,
    crmIdProperty
  });
  const {
    data: associatedCampaigns
  } = useFetchAssociatedCampaigns({
    objectType,
    objectId,
    associationSettings,
    isOneToMany: isMultiSelect
  });
  useEffect(() => {
    if (associatedCampaigns) {
      setSelectedCampaignGuids(associatedCampaigns.map(campaign => campaign.properties.hs_origin_asset_id.value));
      setHasEverLoaded(true);
    } else {
      setSelectedCampaignGuids([]);
    }
  }, [associatedCampaigns]);

  // Automatically save the selected campaign when it changes.
  const handleCampaignChange = useHandleCampaignChange({
    objectType,
    onChange,
    save,
    selectedCampaignGuids,
    selectedAssetCrmId: objectId,
    setSelectedCampaignGuids,
    onSuccess: () => {
      FloatingAlertStore.addAlert({
        message: I18n.text('campaignSelect.success.platformMessage', {
          asset: objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm.toLowerCase()
        }),
        titleText: I18n.text('campaignSelect.success.title'),
        type: 'success'
      });
    },
    onFailure: () => {
      FloatingAlertStore.addAlert({
        message: I18n.text('campaignSelect.failure.platformMessage', {
          asset: objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm.toLowerCase()
        }),
        titleText: I18n.text('campaignSelect.failure.title'),
        type: 'danger'
      });
    }
  });
  if (!associationSettings || !associationSettings[objectType] || !objectDefinition || !hasEverLoaded && assetId) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [withUIFloatingAlertList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "campaigns-lib-floating-alert-list"
    }), /*#__PURE__*/_jsx(CampaignSelectFormControl, {
      hasCampaignsReadAccess: hasCampaignsReadAccess(associationSettings, objectType),
      hasCampaignsWriteAccess: hasCampaignsWriteAccess(associationSettings, objectType),
      value: isMultiSelect ? selectedCampaignGuids : selectedCampaignGuids[0],
      onChange: handleCampaignChange,
      assetObjectType: objectType,
      editorSelect: editorSelect,
      multi: isMultiSelect,
      formControlProps: Object.assign({
        label: /*#__PURE__*/_jsx(CampaignsPlatformSelectLabel, {
          associationSettings: associationSettings,
          objectType: objectType
        }),
        FormLabel
      }, formControlStyle),
      formHelpAsParagraph: Boolean(FormLabel),
      assetLabel: objectDefinition.singularForm.toLowerCase(),
      disabled: disabled,
      disabledTooltip: disabledTooltip
    })]
  });
}
export default /*#__PURE__*/memo(CampaignsPlatformSelect);